import { useMemo, useRef, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { getSectorColor } from "@/lib/sectorColors";

interface Props {
  result: {
    tickers: string[];
    matrix: number[][];
    period: string;
    dataPoints: number;
  };
  sectorMap?: Record<string, string>;
}

interface MergeStep {
  left: number;
  right: number;
  distance: number;
  newIdx: number;
}

interface DendroNode {
  id: number;
  label?: string;
  left?: DendroNode;
  right?: DendroNode;
  distance: number;
  // layout
  x: number;
  y: number;
  leafCount: number;
}

function buildDendrogram(tickers: string[], matrix: number[][]): { root: DendroNode; merges: MergeStep[] } {
  const n = tickers.length;
  // Distance = 1 - correlation
  const dist: number[][] = Array.from({ length: n }, (_, i) =>
    Array.from({ length: n }, (_, j) => (i === j ? 0 : 1 - matrix[i][j]))
  );

  // Each ticker starts as its own cluster
  const active = new Set<number>();
  const clusterMembers: number[][] = [];
  for (let i = 0; i < n; i++) {
    active.add(i);
    clusterMembers.push([i]);
  }

  const merges: MergeStep[] = [];
  let nextIdx = n;

  // Average-linkage agglomerative clustering
  while (active.size > 1) {
    let minDist = Infinity, mergeA = -1, mergeB = -1;
    const ids = [...active];
    for (let ai = 0; ai < ids.length; ai++) {
      for (let bi = ai + 1; bi < ids.length; bi++) {
        const a = ids[ai], b = ids[bi];
        let sum = 0;
        for (const mi of clusterMembers[a])
          for (const mj of clusterMembers[b])
            sum += dist[mi][mj];
        const avg = sum / (clusterMembers[a].length * clusterMembers[b].length);
        if (avg < minDist) { minDist = avg; mergeA = a; mergeB = b; }
      }
    }
    active.delete(mergeA);
    active.delete(mergeB);
    clusterMembers.push([...clusterMembers[mergeA], ...clusterMembers[mergeB]]);
    merges.push({ left: mergeA, right: mergeB, distance: minDist, newIdx: nextIdx });
    active.add(nextIdx);
    nextIdx++;
  }

  // Build tree
  const nodeMap = new Map<number, DendroNode>();
  for (let i = 0; i < n; i++) {
    nodeMap.set(i, { id: i, label: tickers[i], distance: 0, x: 0, y: 0, leafCount: 1 });
  }
  for (const m of merges) {
    const left = nodeMap.get(m.left)!;
    const right = nodeMap.get(m.right)!;
    nodeMap.set(m.newIdx, {
      id: m.newIdx,
      left,
      right,
      distance: m.distance,
      x: 0,
      y: 0,
      leafCount: left.leafCount + right.leafCount,
    });
  }

  const root = nodeMap.get(nextIdx - 1)!;
  return { root, merges };
}

function layoutTree(node: DendroNode, leafIdx: { val: number }, maxDist: number, canvasH: number, marginTop: number, marginBottom: number) {
  const usableH = canvasH - marginTop - marginBottom;
  if (!node.left && !node.right) {
    node.x = leafIdx.val;
    leafIdx.val++;
    node.y = marginTop; // leaves at top
    return;
  }
  if (node.left) layoutTree(node.left, leafIdx, maxDist, canvasH, marginTop, marginBottom);
  if (node.right) layoutTree(node.right, leafIdx, maxDist, canvasH, marginTop, marginBottom);
  node.x = ((node.left?.x ?? 0) + (node.right?.x ?? 0)) / 2;
  node.y = marginTop + (node.distance / maxDist) * usableH;
}

function getClusterColor(idx: number): string {
  const colors = [
    "hsl(220, 70%, 55%)", "hsl(340, 65%, 55%)", "hsl(160, 60%, 45%)",
    "hsl(35, 75%, 50%)", "hsl(270, 55%, 55%)", "hsl(190, 65%, 45%)",
    "hsl(10, 70%, 55%)", "hsl(130, 55%, 45%)",
  ];
  return colors[idx % colors.length];
}

export function CorrelationDendrogram({ result, sectorMap }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [cutoff, setCutoff] = useState([0.5]);
  const isDark = document.documentElement.classList.contains("dark");

  const { root, maxDist, leafOrder } = useMemo(() => {
    const { root, merges } = buildDendrogram(result.tickers, result.matrix);
    const maxDist = merges.length > 0 ? Math.max(...merges.map(m => m.distance), 0.01) : 1;
    // Collect leaf order
    const leaves: string[] = [];
    const collectLeaves = (n: DendroNode) => {
      if (!n.left && !n.right) { leaves.push(n.label!); return; }
      if (n.left) collectLeaves(n.left);
      if (n.right) collectLeaves(n.right);
    };
    collectLeaves(root);
    return { root, maxDist, leafOrder: leaves };
  }, [result]);

  // Identify clusters at cutoff
  const clusters = useMemo(() => {
    const cutDist = cutoff[0] * maxDist;
    const groups: string[][] = [];
    const walk = (n: DendroNode) => {
      if (n.distance <= cutDist || (!n.left && !n.right)) {
        const leaves: string[] = [];
        const collect = (nd: DendroNode) => {
          if (!nd.left && !nd.right) leaves.push(nd.label!);
          else { if (nd.left) collect(nd.left); if (nd.right) collect(nd.right); }
        };
        collect(n);
        groups.push(leaves);
        return;
      }
      if (n.left) walk(n.left);
      if (n.right) walk(n.right);
    };
    walk(root);
    return groups;
  }, [root, cutoff, maxDist]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const n = result.tickers.length;
    const marginLeft = 60;
    const marginRight = 30;
    const marginTop = 50;
    const marginBottom = 80;
    const usableW = w - marginLeft - marginRight;
    const leafSpacing = n > 1 ? usableW / (n - 1) : usableW / 2;

    // Layout
    const leafIdx = { val: 0 };
    layoutTree(root, leafIdx, maxDist, h, marginTop, marginBottom);

    const textColor = isDark ? "#cbd5e1" : "#334155";
    const bgColor = isDark ? "#1a1a2e" : "#ffffff";
    const gridColor = isDark ? "rgba(148,163,184,0.12)" : "rgba(100,116,139,0.1)";

    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, w, h);

    // Build leaf-to-cluster color map
    const leafColorMap = new Map<string, string>();
    clusters.forEach((group, gi) => {
      group.forEach(t => leafColorMap.set(t, getClusterColor(gi)));
    });

    // Draw cutoff line
    const cutY = marginTop + cutoff[0] * (h - marginTop - marginBottom);
    ctx.setLineDash([6, 4]);
    ctx.strokeStyle = isDark ? "rgba(239,68,68,0.6)" : "rgba(220,38,38,0.5)";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(marginLeft - 10, cutY);
    ctx.lineTo(w - marginRight, cutY);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.font = "10px sans-serif";
    ctx.fillStyle = "hsl(0,70%,50%)";
    ctx.textAlign = "right";
    const cutCorr = 1 - cutoff[0] * maxDist;
    ctx.fillText(`cut: corr=${cutCorr.toFixed(2)}`, marginLeft - 14, cutY + 4);

    // Y-axis labels (distance / correlation)
    ctx.font = "9px sans-serif";
    ctx.fillStyle = textColor;
    ctx.textAlign = "right";
    for (let i = 0; i <= 5; i++) {
      const frac = i / 5;
      const yy = marginTop + frac * (h - marginTop - marginBottom);
      const dist = frac * maxDist;
      const corr = 1 - dist;
      ctx.fillStyle = gridColor;
      ctx.fillRect(marginLeft, yy, usableW, 0.5);
      ctx.fillStyle = textColor;
      ctx.fillText(corr.toFixed(2), marginLeft - 6, yy + 3);
    }
    ctx.textAlign = "left";
    ctx.save();
    ctx.translate(12, h / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.font = "10px sans-serif";
    ctx.fillStyle = textColor;
    ctx.textAlign = "center";
    ctx.fillText("Correlation", 0, 0);
    ctx.restore();

    // Draw tree
    const drawNode = (node: DendroNode) => {
      const nx = marginLeft + node.x * leafSpacing;
      const ny = node.y;

      if (node.left && node.right) {
        const lx = marginLeft + node.left.x * leafSpacing;
        const ly = node.left.y;
        const rx = marginLeft + node.right.x * leafSpacing;
        const ry = node.right.y;

        // Determine color: if all leaves in same cluster, use cluster color
        const nodeLeaves: string[] = [];
        const collectL = (nd: DendroNode) => {
          if (!nd.left && !nd.right) nodeLeaves.push(nd.label!);
          else { if (nd.left) collectL(nd.left); if (nd.right) collectL(nd.right); }
        };
        collectL(node);
        const firstColor = leafColorMap.get(nodeLeaves[0]);
        const sameCluster = nodeLeaves.every(l => leafColorMap.get(l) === firstColor);
        const lineColor = sameCluster && firstColor ? firstColor : (isDark ? "hsl(215, 20%, 55%)" : "hsl(215, 25%, 65%)");

        ctx.strokeStyle = lineColor;
        ctx.lineWidth = 1.5;

        // U-shape: vertical from left child up to merge height, horizontal, vertical down to right child
        ctx.beginPath();
        ctx.moveTo(lx, ly);
        ctx.lineTo(lx, ny);
        ctx.lineTo(rx, ny);
        ctx.lineTo(rx, ry);
        ctx.stroke();

        drawNode(node.left);
        drawNode(node.right);
      } else {
        // Leaf node — use sector color if available, else cluster color
        const sectorColors = sectorMap ? getSectorColor(sectorMap[node.label!], isDark) : null;
        const nodeColor = sectorColors ? sectorColors.stroke : (leafColorMap.get(node.label!) || textColor);
        const dotColor = sectorColors ? sectorColors.stroke : (leafColorMap.get(node.label!) || textColor);
        
        ctx.beginPath();
        ctx.arc(nx, ny, 4, 0, Math.PI * 2);
        ctx.fillStyle = dotColor;
        ctx.fill();
        if (sectorColors) {
          ctx.strokeStyle = sectorColors.stroke;
          ctx.lineWidth = 1.5;
          ctx.stroke();
        }

        ctx.save();
        ctx.translate(nx, ny - 8);
        ctx.rotate(-Math.PI / 4);
        ctx.font = `${Math.max(9, 12 - n * 0.15)}px monospace`;
        ctx.fillStyle = nodeColor;
        ctx.textAlign = "left";
        ctx.fillText(node.label!, 0, 0);
        ctx.restore();
      }
    };

    drawNode(root);

    // Bottom legend — sector legend if available, else cluster legend
    ctx.font = "10px sans-serif";
    let lx = marginLeft;
    const ly = h - 20;
    
    if (sectorMap && Object.keys(sectorMap).length > 0) {
      const sectors = [...new Set(result.tickers.map(t => sectorMap[t]).filter(Boolean))];
      sectors.forEach(sector => {
        const sc = getSectorColor(sector, isDark);
        ctx.fillStyle = sc.stroke;
        ctx.fillRect(lx, ly - 8, 10, 10);
        ctx.fillStyle = textColor;
        ctx.textAlign = "left";
        ctx.fillText(sector!, lx + 14, ly);
        lx += ctx.measureText(sector!).width + 28;
      });
    } else {
      clusters.forEach((group, gi) => {
        const color = getClusterColor(gi);
        ctx.fillStyle = color;
        ctx.fillRect(lx, ly - 8, 10, 10);
        ctx.fillStyle = textColor;
        ctx.textAlign = "left";
        const label = group.length <= 3 ? group.join(", ") : `${group.slice(0, 2).join(", ")} +${group.length - 2}`;
        ctx.fillText(label, lx + 14, ly);
        lx += ctx.measureText(label).width + 30;
      });
    }
  }, [result, root, maxDist, cutoff, clusters, isDark, sectorMap]);

  const exportPng = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = "correlation-dendrogram.png";
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Dendrogram</CardTitle>
        <CardDescription>
          Hierarchical clustering tree — closer merges = higher correlation · Adjust the cut line to define cluster groups
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-3 min-w-[280px]">
            <Label className="text-xs whitespace-nowrap">Cut level</Label>
            <Slider value={cutoff} onValueChange={setCutoff} min={0.05} max={0.95} step={0.05} className="w-40" />
            <Badge variant="secondary" className="text-xs font-mono">
              corr ≥ {(1 - cutoff[0] * maxDist).toFixed(2)}
            </Badge>
          </div>
          <Badge variant="outline" className="text-xs">{clusters.length} clusters</Badge>
          <div className="flex flex-wrap gap-2">
            {clusters.map((g, i) => (
              <Badge key={i} variant="secondary" className="text-[10px] font-mono" style={{ borderLeft: `3px solid ${getClusterColor(i)}` }}>
                {g.join(", ")}
              </Badge>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={exportPng} className="ml-auto">
            <Download className="h-3.5 w-3.5 mr-1" /> PNG
          </Button>
        </div>
        <canvas
          ref={canvasRef}
          className="w-full rounded-lg border border-border"
          style={{ height: 500 }}
        />
      </CardContent>
    </Card>
  );
}
